import { useState, useEffect } from 'react';
import { X, Download, Share, Smartphone } from 'lucide-react';
import { useTheme } from './ThemeContext';

// Capture beforeinstallprompt at the global level so we never miss it
// (the event can fire before React mounts)
let globalDeferredPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  globalDeferredPrompt = e;
});

/**
 * InstallPrompt - Shows install prompt for PWA on different devices
 * - Android: Direct "Install" button using native prompt
 * - iOS: Instructions to use Share > Add to Home Screen
 * - Desktop: Install card in bottom-right corner
 */
export default function InstallPrompt() {
  const { colors } = useTheme();
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(globalDeferredPrompt);
  const [platform, setPlatform] = useState('unknown');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

    // If running as installed PWA, mark it and bail
    if (isStandalone) {
      localStorage.setItem('pwa_installed', 'true');
      return;
    }

    // If previously marked as installed but no longer standalone, user uninstalled — clear the flag
    if (localStorage.getItem('pwa_installed') === 'true' && !isStandalone) {
      localStorage.removeItem('pwa_installed');
    }

    // Check if already dismissed recently
    const lastDismissed = localStorage.getItem('install_prompt_dismissed');
    if (lastDismissed) {
      const daysSince = (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) {
        return;
      }
    }

    // Detect platform
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) {
      setPlatform('ios');
    } else if (/android/.test(ua)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // Pick up the global deferred prompt if it was captured before mount
    if (globalDeferredPrompt) {
      setDeferredPrompt(globalDeferredPrompt);
    }

    // Listen for future beforeinstallprompt events
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      globalDeferredPrompt = e;
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show prompt after delay
    const timer = setTimeout(() => {
      setShowPrompt(true);
    }, 5000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(timer);
    };
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('install_prompt_dismissed', Date.now().toString());
  };

  const handleInstall = async () => {
    // Try the native prompt first (works on Android Chrome, Edge, etc.)
    const prompt = deferredPrompt || globalDeferredPrompt;
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem('pwa_installed', 'true');
        setShowPrompt(false);
      }
      globalDeferredPrompt = null;
      setDeferredPrompt(null);
    }
  };

  if (!showPrompt || dismissed) return null;

  // iOS Safari - show instructions (no native install API)
  if (platform === 'ios') {
    return (
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#1a1a1a',
        borderTop: `2px solid ${colors.accent}`,
        padding: '1rem',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
        zIndex: 10000,
        animation: 'slideUp 0.3s ease-out'
      }}>
        <style>{`
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
        `}</style>
        <button
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            background: 'none',
            border: 'none',
            color: '#888',
            cursor: 'pointer',
            padding: '0.5rem',
            minHeight: '44px',
            minWidth: '44px'
          }}
        >
          <X size={20} />
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: colors.accent,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Smartphone size={24} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#e0e0e0', fontSize: '1rem' }}>
              Install Work Van
            </h4>
            <p style={{ margin: 0, color: '#888', fontSize: '0.85rem', lineHeight: '1.5' }}>
              Tap <Share size={14} style={{ verticalAlign: 'middle', color: '#007AFF' }} /> then
              <strong style={{ color: '#e0e0e0' }}> "Add to Home Screen"</strong> for the best experience.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Android - only show when we have the native install prompt
  if (platform === 'android') {
    if (!(deferredPrompt || globalDeferredPrompt)) return null;

    return (
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#1a1a1a',
        borderTop: `2px solid ${colors.accent}`,
        padding: '1rem',
        zIndex: 10000,
        animation: 'slideUp 0.3s ease-out'
      }}>
        <style>{`
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
        `}</style>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: colors.accent,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Smartphone size={24} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 0.25rem 0', color: '#e0e0e0', fontSize: '1rem' }}>
              Install Work Van
            </h4>
            <p style={{ margin: 0, color: '#888', fontSize: '0.85rem' }}>
              Get quick access from your home screen
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleDismiss}
              style={{
                padding: '0.75rem 1rem',
                background: '#2a2a2a',
                border: 'none',
                borderRadius: '8px',
                color: '#888',
                fontSize: '0.9rem',
                cursor: 'pointer',
                minHeight: '48px'
              }}
            >
              Later
            </button>
            <button
              onClick={handleInstall}
              style={{
                padding: '0.75rem 1.25rem',
                background: colors.accent,
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                minHeight: '48px'
              }}
            >
              <Download size={16} />
              Install
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop with native prompt
  if (platform === 'desktop' && deferredPrompt) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '1rem',
        right: '1rem',
        background: '#1a1a1a',
        border: '1px solid #3a3a3a',
        borderRadius: '12px',
        padding: '1rem',
        zIndex: 10000,
        maxWidth: '320px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        animation: 'slideIn 0.3s ease-out'
      }}>
        <style>{`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}</style>
        <button
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            background: 'none',
            border: 'none',
            color: '#888',
            cursor: 'pointer',
            padding: '0.25rem'
          }}
        >
          <X size={16} />
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: colors.accent,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Download size={20} color="#fff" />
          </div>
          <div>
            <h4 style={{ margin: '0 0 0.25rem 0', color: '#e0e0e0', fontSize: '0.95rem' }}>
              Install Work Van
            </h4>
            <p style={{ margin: 0, color: '#888', fontSize: '0.8rem' }}>
              Quick access from your desktop
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleDismiss}
            style={{
              flex: 1,
              padding: '0.625rem',
              background: '#2a2a2a',
              border: 'none',
              borderRadius: '6px',
              color: '#888',
              fontSize: '0.85rem',
              cursor: 'pointer'
            }}
          >
            Not now
          </button>
          <button
            onClick={handleInstall}
            style={{
              flex: 1,
              padding: '0.625rem',
              background: colors.accent,
              border: 'none',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '0.85rem',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Install
          </button>
        </div>
      </div>
    );
  }

  return null;
}
